import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    colors, fontSizes, lineHeights, space, fontWeights, radii, mediaQueries
} from 'style/config';
import KlarnaMarketing from 'components/ProductPage/KlarnaMarketing/KlarnaMarketing';
import skuUtils from 'utils/Sku';
import languageLocale from 'utils/LanguageLocale';
import anaConsts from 'analytics/constants';
import RwdProductPageEN from 'components/ProductPage/locales/RwdProductPage_en_US';
import RwdProductPageFR from 'components/ProductPage/locales/RwdProductPage_fr_CA';
import basketUtils from 'utils/Basket';

function getPrice(sku) {
    const { isBirthdayGiftSkuValidationEnabled } = Sephora.configurationSettings;
    const getText = (...args) => languageLocale.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR)(...args);

    let price = '';

    if (skuUtils.isFree(sku)) {
        price = (
            <span>
                {getText('free')}
                {isBirthdayGiftSkuValidationEnabled ? <sup>∫</sup> : null}
            </span>
        );
    } else if (sku.biType && !skuUtils.isRougeRewardCard(sku)) {
        price = sku.biType.toLowerCase();
    } else {
        price = sku.listPrice;
    }

    return price;
}

function formatValueText(rawText) {
    const innerText = rawText
        .trim()
        .replace(/^\((.*)\)$/, '$1')
        .trim();
    const parts = innerText.split(/\s+/);

    const amount = parts[0];
    const label = parts.slice(1).join(' ').toUpperCase();

    return `${amount} ${label}`;
}

function calculateDiscountedPrice(priceStr, discountPercent) {
    if (!priceStr) {
        return null;
    }

    const rawAmount = basketUtils.removeCurrency(priceStr);
    const numericValue = parseFloat(rawAmount);

    if (isNaN(numericValue)) {
        return null;
    }

    const discounted = (numericValue * (1 - discountPercent / 100)).toFixed(2);

    if (languageLocale.isFrench()) {
        return `${discounted.replace('.', ',')} $`;
    }

    return `$${discounted}`;
}

const PROMO_DISCOUNT_PERCENT = 30;
const PROMO_CODE = 'SAVESC';

/* eslint-disable-next-line complexity */
function Price({
    sku,
    isSmallView,
    isReplenishmentEligible,
    replenishMessage,
    isExternallySellable,
    isCanada,
    isUsa,
    showPDPTopOfPageRedesign,
    showFulfillmentMethodsRedesign = false,
    showDiscountedPriceWithCode
}) {
    const getText = (...args) => languageLocale.getLocalisedDataFromResource(RwdProductPageEN, RwdProductPageFR)(...args);
    const { salePrice, listPrice, valuePrice } = sku;
    const isAfterpayEnabled = Sephora.configurationSettings.afterpayEnabled && sku.actionFlags?.isAfterpayEligible;
    const isKlarnaPaymentEnabled = Sephora.configurationSettings.isKlarnaPaymentEnabled && sku.actionFlags?.isKlarnaEligible;
    const isPayPalPayLaterEligibleEnabled = Sephora.configurationSettings.isPayPalEnabled && sku.actionFlags?.isPayPalPayLaterEligible;
    const formattedValuePrice = isUsa && valuePrice && formatValueText(valuePrice);
    const pricingWrapperStyle = [styles.pricingWrapper, showPDPTopOfPageRedesign && styles.pricingWrapperRedesign];
    const salePriceStyle = [styles.salePrice, showPDPTopOfPageRedesign && styles.priceRedesign];

    const discountedPrice = showDiscountedPriceWithCode ? calculateDiscountedPrice(salePrice || listPrice, PROMO_DISCOUNT_PERCENT) : null;
    const showPromoPrice = showDiscountedPriceWithCode && Boolean(discountedPrice);

    return (
        <>
            <p css={styles.root}>
                <span
                    css={[
                        styles.inner,
                        (salePrice || valuePrice || showPromoPrice) && styles.innerSale,
                        {
                            flexWrap: (isReplenishmentEligible && !showFulfillmentMethodsRedesign) || showPDPTopOfPageRedesign ? 'wrap' : 'nowrap',
                            paddingBottom: isSmallView && isCanada ? space[2] : '0'
                        }
                    ]}
                >
                    <span css={pricingWrapperStyle}>
                        {salePrice && (
                            <>
                                <b
                                    css={salePriceStyle}
                                    children={salePrice}
                                />{' '}
                            </>
                        )}
                        <b
                            css={[salePrice && styles.priceStrikethrough, showPDPTopOfPageRedesign && styles.priceStrikeRedesign]}
                            children={getPrice(sku)}
                        />
                        {showPromoPrice ? (
                            <>
                                {' '}
                                <span css={styles.promoPrice}>{getText('discountedPriceWithCode', [discountedPrice, PROMO_CODE])}</span>
                            </>
                        ) : (
                            valuePrice && (
                                <>
                                    {' '}
                                    {!isUsa ? (
                                        <span children={valuePrice} />
                                    ) : (
                                        <span
                                            css={[styles.valuePrice, showPDPTopOfPageRedesign && styles.valuePriceRedesign]}
                                            children={formattedValuePrice}
                                        />
                                    )}
                                </>
                            )
                        )}
                    </span>
                    {isExternallySellable ? null : (
                        <>
                            {(isKlarnaPaymentEnabled || isAfterpayEnabled || isPayPalPayLaterEligibleEnabled) && (
                                <KlarnaMarketing
                                    isSmallView={isSmallView}
                                    showPDPTopOfPageRedesign={showPDPTopOfPageRedesign}
                                    isAfterpayEnabled={isAfterpayEnabled}
                                    isKlarnaEnabled={isKlarnaPaymentEnabled}
                                    isPayPalPayLaterEligibleEnabled={isPayPalPayLaterEligibleEnabled}
                                    sku={{
                                        listPrice,
                                        salePrice
                                    }}
                                    analyticsPageType={anaConsts.PAGE_TYPES.PRODUCT}
                                    analyticsContext={anaConsts.CONTEXT.PRODUCTPAGE}
                                />
                            )}
                        </>
                    )}
                </span>
            </p>
            {isReplenishmentEligible && !showFulfillmentMethodsRedesign && replenishMessage()}
        </>
    );
}

const styles = {
    root: {
        display: 'flex',
        alignItems: 'center',
        fontSize: fontSizes.md,
        lineHeight: lineHeights.tight,
        minHeight: 35
    },
    inner: {
        display: 'flex',
        alignItems: 'baseline'
    },
    innerSale: {
        flexDirection: 'column'
    },
    pricingWrapper: {
        marginRight: '.25em',
        minWidth: 'auto',
        whiteSpace: 'nowrap'
    },
    pricingWrapperRedesign: {
        fontSize: fontSizes['lg-bg'],
        textAlign: 'left'
    },
    salePrice: {
        color: colors.red
    },
    priceRedesign: {
        fontWeight: fontWeights.bold,
        fontSize: fontSizes['lg-bg'],
        [mediaQueries.sm]: {
            fontSize: fontSizes['xl-bg']
        }
    },
    priceStrikeRedesign: {
        fontSize: fontSizes['lg-bg'],
        [mediaQueries.sm]: {
            fontSize: fontSizes['xl-bg']
        }
    },
    priceStrikethrough: {
        textDecoration: 'line-through',
        fontWeight: 'var(--font-weight-normal)'
    },
    valuePrice: {
        backgroundColor: colors.red,
        color: colors.white,
        fontWeight: fontWeights.bold,
        fontSize: fontSizes.xs,
        padding: `${space[1]}px ${space[2]}px`,
        borderRadius: radii[2],
        verticalAlign: 'middle',
        position: 'relative',
        lineHeight: lineHeights.relaxed,
        top: '-1px'
    },
    //custom values to fit figma design for redesign variant
    valuePriceRedesign: {
        padding: `${space[1] / 2}px ${space[2]}px`,
        top: '-2px'
    },
    promoPrice: {
        backgroundColor: colors.red,
        color: colors.white,
        fontWeight: fontWeights.bold,
        fontSize: fontSizes.sm,
        padding: `${space[1]}px ${space[2]}px`,
        borderRadius: radii[2],
        verticalAlign: 'middle',
        position: 'relative',
        lineHeight: lineHeights.relaxed,
        top: '-1px'
    }
};

export default wrapFunctionalComponent(Price, 'Price');
